import { armOf } from "@bing/engine-mensch";
import { isGameId, type GameId } from "@bing/shared";
import { DIAMOND_SEAT_EDGES, pasurSeatEdge, seatEdge, type SeatEdge } from "./card-motion";
import { viewTurns } from "./mensch-board";

/**
 * WHERE EACH GAME'S OWN TABLE DRAWS A SEAT — as an angle around the viewer, so the waiting room
 * can seat every player exactly where the deal will (operator, 2026-09-29: «nobody moves between
 * the waiting room and the deal»). Degrees on screen axes (y grows DOWN): 90 is the bottom (you),
 * 180 your left, 270 across, 0 your right.
 *
 * ⚠️ THE TABLES DO NOT AGREE WITH EACH OTHER, which is the whole reason this is per game — the ring
 * used to draw seat+1 on your LEFT for everyone:
 *   · حکم, بی‌دل, شلم, پاسور — the diamond, seat+1 on your RIGHT: `DIAMOND_SEAT_EDGES`, the map their
 *     cards fly in from (their seats are pinned the same way). شلم drew seat+1 on your LEFT off a map
 *     of its own until P0-7 (2026-10-09); پاسور's partner sits across since P1 Task 3 (R3), and its
 *     table for TWO (2026-10-20) seats the one opponent across — the table's own `pasurSeatEdge`.
 *   · منچ — the board spins by ARM (`viewTurns`) so YOUR arm is at the bottom and the others keep
 *     theirs, so with three players the empty arm falls on a side that depends on your seat.
 *   · بلوف — no sides at all: every opponent in ONE row along the top, in ABSOLUTE seat order, and
 *     the row runs right-to-left (`dir="rtl"`), so the lowest seat is the rightmost. The ring keeps
 *     that left-to-right order around its top.
 *   · شطرنج, تخته نرد, دوز — two seats: the opponent sits across on every table.
 *   · مار پله — no sides at all: one shared board and a strip of players below it, so the waiting
 *     room keeps its own ring (a table for two: the opponent across).
 * `apps/web/test/waiting-room-seat-sides.test.tsx` renders each of those TABLES beside the waiting
 * room from every chair, so a table that changes its layout fails there until this follows it.
 */
type Layout = (seat: number, you: number, seats: number) => number;

const EDGE_ANGLE: Record<SeatEdge, number> = { bottom: 90, left: 180, top: 270, right: 0 };

/** A table's own four-edge map (the one its cards fly in from), read as an angle. */
const byEdges =
  (edges: readonly SeatEdge[]): Layout =>
  (seat, you, seats) =>
    EDGE_ANGLE[seatEdge(edges, (seat - you + seats) % seats)];

/** Evenly round the felt, clockwise from your left — the waiting room's own ring. */
const ring: Layout = (seat, you, seats) => 90 + ((seat - you + seats) % seats) * (360 / seats);

/** منچ: the arm the board draws a seat on once it is spun (0 left, 1 top, 2 right, 3 = yours). */
const arms: Layout = (seat, you, seats) =>
  180 + ((armOf(seat, seats) + viewTurns(you, seats)) % 4) * 90;

/** بلوف: the top row's left-to-right order — DESCENDING seat, the row being RTL. */
const row: Layout = (seat, you, seats) => {
  if (seat === you) return 90;
  const leftToRight = [...Array(seats).keys()].filter((s) => s !== you).sort((a, b) => b - a);
  return 90 + (leftToRight.indexOf(seat) + 1) * (360 / seats);
};

/** Every game, by name — a `Record` over `GameId`, so a new game cannot be forgotten here. */
export const TABLE_SEAT_LAYOUT: Record<GameId, Layout> = {
  hokm: byEdges(DIAMOND_SEAT_EDGES),
  hearts: byEdges(DIAMOND_SEAT_EDGES),
  shelem: byEdges(DIAMOND_SEAT_EDGES),
  mensch: arms,
  ladders: ring,
  pasur: (seat, you, seats) => EDGE_ANGLE[pasurSeatEdge(seat, you, seats)],
  bluff: row,
  chess: ring,
  backgammon: ring,
  dooz: ring,
};

/** The angle `gameId`'s table draws `seat` at, seen from `you` (an unknown game keeps the ring). */
export function tableSeatAngle(gameId: string, seat: number, you: number, seats: number): number {
  const n = Math.max(seats, 1);
  const layout = isGameId(gameId) ? TABLE_SEAT_LAYOUT[gameId] : ring;
  return layout(seat, you, n) % 360;
}
